import { categoryPricesForRate, slotDateISO } from "./bokun-test/pricing";
import { freeCancellationHours } from "./card-policy";
import { resolveBokunPolicy, type PolicySnapshot } from "./bokun-policy";
import { cancellationDeadline, type BookingPlace } from "./booking-logistics";

export interface CalendarCategory { id: string; title: string; minAge: number | null; maxAge: number | null; ticketCategory: string | null }
export interface CalendarSlot {
  date: string; startTimeId: number | null; startTime: string | null; rateId: string | null;
  available: boolean; soldOut: boolean; unlimited: boolean; capacity: number | null; minParticipants: number;
  prices: { id: string; amount: number; currency: string; minRequired: number | null }[];
  rateTitle?: string | null; pickupPricingType?: string | null; dropoffPricingType?: string | null;
  freeCancellationHours?: number | null; cancellationDeadline?: string | null;
  policySnapshot?: PolicySnapshot;
}
export interface PreviewCalendarData { categories: CalendarCategory[]; slots: CalendarSlot[]; start: string; end: string; pickupRequired: boolean; dropoffRequired: boolean; pickupAvailable?: boolean; dropoffAvailable?: boolean; perBooking: boolean; pickupPlaces?: BookingPlace[]; dropoffPlaces?: BookingPlace[]; timeZone?: string | null }
export const previewSelectorEnabled = (v: string | null | undefined) => v === "1";
/** Category-unit prices are per passenger even when ticketPerPerson is false. */
export function calendarPerBooking(raw: unknown): boolean {
  return Array.isArray(raw) && raw.some(r => Array.isArray(r.pricesByRate) && r.pricesByRate.some((p: Record<string, unknown>) => {
    const units = Array.isArray(p.pricePerCategoryUnit) ? p.pricePerCategoryUnit : [];
    return units.length === 0 && p.pricePerBooking != null;
  }));
}
export function calendarRange(now = new Date()) {
  const start = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Cancun", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  const end = new Date(`${start}T12:00:00Z`); end.setUTCDate(end.getUTCDate() + 59);
  return { start, end: end.toISOString().slice(0, 10) };
}
const number = (v: unknown) => typeof v === "number" && Number.isFinite(v) ? v : null;
export function normalizeCalendarSlots(raw: unknown, productDefaultRate: unknown, product: Record<string, unknown> = {}, language = "es"): CalendarSlot[] {
  if (!Array.isArray(raw)) throw new Error("Invalid calendar");
  return raw.flatMap((r: Record<string, unknown>) => {
    const date = slotDateISO(r); if (!date) return [];
    const defaultRate = r.defaultRateId ?? productDefaultRate;
    const rates = (Array.isArray(r.rates) ? r.rates : Array.isArray(product.rates) ? product.rates : []) as Record<string, unknown>[];
    const offered = rates.length ? rates : [{ id: defaultRate }];
    // Default first preserves existing initial selection while exposing other real rates.
    offered.sort((a,b)=>Number(String(b.id)===String(defaultRate))-Number(String(a.id)===String(defaultRate)));
    return offered.map(rate => {
    const rateId = rate.id == null ? null : String(rate.id);
    const prices = categoryPricesForRate(Array.isArray(r.pricesByRate) ? r.pricesByRate : [], rateId)
      .map(p => ({ id: p.pricingCategoryId, amount: p.amount, currency: p.currency, minRequired: p.minParticipantsRequired }));
    const unlimited = r.unlimitedAvailability === true;
    const capacity = number(r.availabilityCount);
    const available = r.unavailable !== true && r.soldOut !== true && (unlimited || (capacity ?? 0) > 0);
    const time = typeof r.startTime === "string" ? r.startTime : null;
    const hours = freeCancellationHours(rate.cancellationPolicy ?? product.cancellationPolicy);
    return { date, rateId, startTimeId: number(r.startTimeId), startTime: time,
      policySnapshot: resolveBokunPolicy(product,{productId:String(product.id ?? ""),rateId,date,startTime:time,language,departure:r}),
      rateTitle: typeof rate.title === "string" ? rate.title : null,
      pickupPricingType: typeof rate.pickupPricingType === "string" ? rate.pickupPricingType : null,
      dropoffPricingType: typeof rate.dropoffPricingType === "string" ? rate.dropoffPricingType : null,
      freeCancellationHours: hours, cancellationDeadline: cancellationDeadline(date,time,hours,typeof product.timeZone === "string" ? product.timeZone : null),
      available, soldOut: r.soldOut === true, unlimited, capacity,
      minParticipants: number(r.minParticipantsToBookNow) ?? number(r.minParticipants) ?? 1, prices };
    });
  });
}
export const isAdult = (c: CalendarCategory) => c.ticketCategory === "ADULT" || /adult/i.test(c.title);
export const initialQuantities = (cs: CalendarCategory[]) => Object.fromEntries(cs.map(c => [c.id, isAdult(c) ? 2 : 0]));
export const changeQuantity = (c: CalendarCategory, qty: number, delta: number) => Math.max(isAdult(c) ? 1 : 0, qty + delta);
export type DayState = "available" | "last" | "full" | "closed";
/**
 * "closed": Bókun no devuelve ningún horario para ese día (tiempo límite de
 * reserva, día sin salida o cerrado). "full": hay horario pero sin cupo.
 */
export function dayState(slots: CalendarSlot[]): DayState {
  if (!slots.length) return "closed";
  const open = slots.filter(s => s.available);
  if (!open.length) return "full";
  if (open.some(s => s.unlimited || (s.capacity ?? 0) > 4)) return "available";
  return "last";
}
export function selectionTotal(slot: CalendarSlot | undefined, qty: Record<string, number>, perBooking = false) {
  const pax = Object.values(qty).reduce((a,b) => a+b, 0);
  const total = Math.round((slot?.prices.reduce((a,p) => a + p.amount * (perBooking ? (qty[p.id] > 0 ? 1 : 0) : (qty[p.id] ?? 0)), 0) ?? 0)*100)/100;
  const overCapacity = !!slot && !slot.unlimited && pax > (slot.capacity ?? 0);
  const priced = !!slot && slot.prices.length > 0 && slot.prices.every(p => p.currency === slot.prices[0]?.currency) && Object.entries(qty).every(([id,n]) => !n || slot.prices.some(p => p.id === id && (p.minRequired == null || n >= p.minRequired)));
  return { pax, total, overCapacity, ready: !!slot?.available && priced && !overCapacity && pax >= slot.minParticipants };
}